// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import TitleTypography from "@hardwoods/App/Components/Common/TitleTypography";
import Notify from "@hardwoods/App/Components/Notify";
import DataTable, { DataTableProps, SortOrderOptions } from "@hardwoods/MobiusOverrides/DataTable/DataTable";
import CardListHeading from "@hardwoods/Overrides/Components/CardListHeading";
import OrderSummaryCard, { OrderSummaryCardStyles } from "@hardwoods/Overrides/Components/OrderSummaryCard";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { GetOrdersApiParameter } from "@insite/client-framework/Services/OrderService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadOrders from "@insite/client-framework/Store/Data/Orders/Handlers/LoadOrders";
import { getOrdersDataView } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    primaryTitle = "primaryTitle",
    secondaryTitle = "secondaryTitle",
    numberOfRecords = "numberOfRecords",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.primaryTitle]: string;
        [fields.secondaryTitle]: string;
        [fields.numberOfRecords]: number;
        [fields.customCSS]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        ordersDataView: getOrdersDataView(state, recentOrdersParameter),
        orderHistoryPageLink: getPageLinkByPageType(state, "OrderHistoryPage"),
        settingsCollection: getSettingsCollection(state),
        canViewOrders: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
    };
};

const mapDispatchToProps = {
    loadOrders,
};

type Props = OwnProps & HasHistory & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentOrdersStyles {
    cardList?: CardListStyles;
    errorGridItem?: GridItemProps;
    errorText?: TypographyPresentationProps;
    noOrdersFoundGridItem?: GridItemProps;
    noOrdersFoundText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    orderSummaryCard?: OrderSummaryCardStyles;

    tableWrapper?: InjectableCss;

    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    dataTableCells?: DataTableCellBaseProps;
    dataCell?: DataTableCellProps;
    reorderButton?: ButtonPresentationProps;
}

export const recentOrdersStyles: RecentOrdersStyles = {
    noOrdersFoundGridItem: {
        width: 12,
        css: css`
            padding-top: 0;
        `,
    },
    
    tableWrapper: {
        css: css`
            width: 100%;
            overflow: auto;
            padding: 0 20px;
        `,
    },
    dataTableHeaders: {
        css: css`
            text-transform: uppercase;
        `,
    },
    cardList: {
        gridContainer: {
            css: css`
                margin: auto;
                a[data-test-selector="cardListHeadingViewAllLink"] {
                    margin-right: 10px;
                    margin-top: 10px;
                }
            `,
        },
    },
    reorderButton: {
        sizeVariant: "small",
        typographyProps: {
            css: css`
                text-transform: uppercase;
            `,
        },
        variant: "tertiary",
        css: css`
            padding: 0 15px;
            display: flex;
            justify-content: center;
            align-items: center;
            margin: 25px auto;
        `,
    },
};

const defaultCustomCss = `.card-list-heading{
    }

    .view-all-link{
    }

    .no-orders-found-grid-item{
    }

    .description-grid-item{
    }

    .no-orders-found-text{
    }

    .card_container__wrapper{
    }

    .order-summary-card-grid-item{
    }

    .order-summary-card-container{
    }

    .page-type-link-grid-item{
    }

    .order-number-grid-item{
    }

    .status-grid-item{
    }

    .web-order-number-grid-item{
    }

    .total-grid-item{
    }`;

let recentOrdersParameter: GetOrdersApiParameter = {
    customerSequence: "-1",
    page: 1,
    sort: "OrderDate DESC",
};

const styles = recentOrdersStyles;

class RecentOrders extends React.Component<Props> {
    componentDidMount() {
        recentOrdersParameter.pageSize = this.props.fields.numberOfRecords;
        if (this.props.canViewOrders && !this.props.ordersDataView.value) {
            this.props.loadOrders(recentOrdersParameter);
        }
    }

    render() {
        const { ordersDataView, fields, orderHistoryPageLink, settingsCollection, canViewOrders } = this.props;
        if (!canViewOrders || !ordersDataView.value) {
            return null;
        }

        const orderHistoryUrl = orderHistoryPageLink ? orderHistoryPageLink.url : undefined;

        const customCssWrapper = {
            css: css`
                ${this.props.fields.customCSS}
            `,
        };

        return (
            <StyledWrapper {...customCssWrapper}>
                <CardList extendedStyles={styles.cardList} data-test-selector="widget_RecentOrders">
                    {(fields.primaryTitle || fields.secondaryTitle) && (
                        <CardListHeading primaryTitle={fields.primaryTitle} secondaryTitle={fields.secondaryTitle} />
                    )}
                    {ordersDataView.value.length === 0 && (
                        <GridItem className="no-orders-found-grid-item" {...styles.noOrdersFoundGridItem}>
                            <Notify messageType="danger" body={translate("No orders found")} />
                        </GridItem>
                    )}

                    {ordersDataView.value.length !== 0 && (
                        <StyledWrapper {...styles.tableWrapper}>
                            <DataTable {...styles.dataTable}>
                                <DataTableHead {...styles.dataTableHead}>
                                    <DataTableHeader {...styles.dataTableHeaders}>{translate("Date")}</DataTableHeader>
                                    <DataTableHeader {...styles.dataTableHeaders}>
                                        {translate("Order Number")}
                                    </DataTableHeader>
                                    <DataTableHeader {...styles.dataTableHeaders}>
                                        {translate("Ship To / Pick Up")}
                                    </DataTableHeader>
                                    <DataTableHeader {...styles.dataTableHeaders}>{translate("Status")}</DataTableHeader>
                                    <DataTableHeader {...styles.dataTableHeaders}>{translate("PO Number")}</DataTableHeader>
                                    <DataTableHeader {...styles.dataTableHeaders}>{translate("Total")}</DataTableHeader>
                                    <DataTableHeader {...styles.dataTableHeaders} title={translate("Reorder")} />
                                </DataTableHead>
                                <DataTableBody {...styles.dataTableBody}>
                                    {ordersDataView.value.map(
                                        order =>
                                            order !== null && (
                                                <DataTableRow
                                                    {...styles.dataTableRow}
                                                    data-test-selector="recentOrders_dataLine"
                                                    key={order.id}
                                                >
                                                    <OrderSummaryCard
                                                        order={order}
                                                        orderSettings={settingsCollection.orderSettings}
                                                        extendedStyles={styles.orderSummaryCard}
                                                    />
                                                </DataTableRow>
                                            ),
                                    )}
                                </DataTableBody>
                            </DataTable>
                            <Button
                                {...styles.reorderButton}
                                onClick={() => this.props.history.push(orderHistoryUrl || "")}
                            >
                                {translate("Show More Orders")}
                            </Button>
                        </StyledWrapper>
                    )}
                </CardList>
            </StyledWrapper>
        );
    }
}

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(RecentOrders)),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.primaryTitle,
                displayName: "Primary Title",
                editorTemplate: "TextField",
                defaultValue: "Recent Orders",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.secondaryTitle,
                displayName: "Secondary Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Orders Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
